import {
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { ClientConfigResolveAppV0Dto } from '../utils/clients/typedClient';
import { typedHttpClient } from '../utils/httpClient';
import { queryClient } from '../utils/queryClient';
import type { ClientConfigGroupLike } from './clientConfigHelpers';

export const clientConfigQueryKeys = {
  appDefaultConfigV0: ['client-config', 'resolve-app-default-v0'] as const,
  allGroupsPaginated: ['client-config', 'groups', 'all-pages'] as const,
};

const CLIENT_CONFIG_GROUPS_PAGE_SIZE = 100;

/** Shared stale window so multiple subscribers reuse one fetch without hammering the API. */
const APP_DEFAULT_STALE_MS = 5 * 60 * 1000;
const ALL_GROUPS_STALE_MS = 5 * 60 * 1000;

export async function fetchClientConfigAppDefaultConfigV0(): Promise<ClientConfigResolveAppV0Dto> {

  const response = await typedHttpClient().clientConfig.clientConfigControllerResolveAppDefaultConfigV0();
  return response.data;

}

export async function fetchAllClientConfigGroupsPages(): Promise<ClientConfigGroupLike[]> {

  const max = CLIENT_CONFIG_GROUPS_PAGE_SIZE;

  let totalItems = -1;
  const allGroups: ClientConfigGroupLike[] = [];
  let offset = 0;
  let emptyRetriesRemaining = 2;

  while (totalItems < 0 || allGroups.length < totalItems) {

    const response = await typedHttpClient().clientConfig.clientConfigControllerPaginateGroups({
      $offset: offset,
      $max: max,
    });
    const batch = response.data?.result || [];

    if (totalItems < 0) {

      const reportedTotal = response.data?.total;
      totalItems = typeof reportedTotal === 'number' && Number.isFinite(reportedTotal) && reportedTotal >= 0
        ? reportedTotal
        : batch.length;

    }

    if (totalItems === 0) {

      break;

    }

    if (batch.length === 0) {

      if (emptyRetriesRemaining <= 0) {

        break;

      }
      emptyRetriesRemaining--;
      continue;

    }

    emptyRetriesRemaining = 2;
    allGroups.push(...(batch as ClientConfigGroupLike[]));
    offset += batch.length;

  }

  return allGroups;

}

export function invalidateClientConfigGroupsFullListQuery() {

  queryClient.invalidateQueries({ queryKey: clientConfigQueryKeys.allGroupsPaginated }).catch((error: unknown) => {

    console.error(error);

  });

}

export function useClientConfigAppDefaultConfigV0(
  options?: Omit<
  UseQueryOptions<ClientConfigResolveAppV0Dto, Error>,
  'queryKey' | 'queryFn'
  >,
) {

  return useQuery({
    queryKey: clientConfigQueryKeys.appDefaultConfigV0,
    queryFn: fetchClientConfigAppDefaultConfigV0,
    staleTime: APP_DEFAULT_STALE_MS,
    ...options,
  });

}

export function useAllClientConfigGroupsQuery(
  options?: Omit<
  UseQueryOptions<ClientConfigGroupLike[], Error>,
  'queryKey' | 'queryFn'
  >,
) {

  return useQuery({
    queryKey: clientConfigQueryKeys.allGroupsPaginated,
    queryFn: fetchAllClientConfigGroupsPages,
    staleTime: ALL_GROUPS_STALE_MS,
    ...options,
  });

}
